import { ADVANCED_STRATEGY, BINDING_STRATEGIES } from '../bindings.js'
import { describeSwitchCase, SWITCH_ELSE_PORT, switchCasePort } from '../graphPorts.js'
import { HASS_PORT_KIND } from './ports.js'

/**
 * Node types of the Home Assistant flow graph. A node's `data` keeps the
 * shape of one existing automation AST unit (AutomationBinding, -Trigger,
 * -Condition, -Action as JSON, without ids and without what a wire carries),
 * so the existing editors, validation and generators apply unchanged.
 */

/**
 * A Home Assistant entity. Actions wired to its `target` input act on it; its
 * `state` output feeds bindings and objects (their Primary Entity).
 */
export const entityNode = {
    kind: 'entity',
    label: 'Entity',
    icon: '◉',
    color: '#0284c7',
    ports: {
        inputs: [{ id: 'target', label: 'Targeted by', accepts: [HASS_PORT_KIND.TARGET] }],
        outputs: [{ id: 'state', label: 'State', provides: HASS_PORT_KIND.ENTITY }],
    },
    properties: {
        entityId: { type: 'string', label: 'Entity' },
    },
    defaults: { entityId: '' },
}

const BINDING_COLOR = '#7c3aed'
const ENTITY_INPUT = Object.freeze({ id: 'entity', label: 'Entity', accepts: [HASS_PORT_KIND.ENTITY], max: 1 })
const CASE_INPUT = Object.freeze({ id: 'case', label: 'Case', accepts: [HASS_PORT_KIND.CASE], max: 1 })
const VALUE_OUTPUT = Object.freeze({ id: 'value', label: 'Value', provides: HASS_PORT_KIND.VALUE })

/*
 * Binding nodes: one per strategy (bindings.js), the node type being the
 * strategy. The bound property is chosen from the bindable properties of the
 * UI object the value is wired to. State and Mapping read an entity: the one
 * wired to `entity`, else the entity of the switch gating them, else the
 * object's Primary Entity. Template and Constant read none. A binding wired
 * to a switch case applies only in that case.
 */
const bindingNode = (strategy, label, icon, readsEntity) => ({
    kind: 'binding',
    strategy,
    label,
    icon,
    color: BINDING_COLOR,
    ports: { inputs: readsEntity ? [ENTITY_INPUT, CASE_INPUT] : [CASE_INPUT], outputs: [VALUE_OUTPUT] },
    properties: {
        property: { type: 'enum', label: 'Property', valuesFrom: 'targetProperties' },
    },
    defaults: { property: '', strategy },
})

export const BINDING_NODE_TYPES = {
    state: bindingNode('state', BINDING_STRATEGIES.state.label, '≡', true),
    mapping: bindingNode('mapping', BINDING_STRATEGIES.mapping.label, '⇄', true),
    constant: bindingNode('constant', BINDING_STRATEGIES.constant.label, '#', false),
    [ADVANCED_STRATEGY.key]: bindingNode(ADVANCED_STRATEGY.key, ADVANCED_STRATEGY.label, '{}', false),
}

export { describeSwitchCase, SWITCH_ELSE_PORT, switchCasePort }
const CASE_OUTPUT = (id, label) => ({ id, label, provides: HASS_PORT_KIND.CASE })

/**
 * Switch: compares an entity's state, or one of its attributes, with its
 * cases in order. The bindings wired to the first matching case apply; those
 * on `else` apply when none matches. The entity is the one wired to `entity`,
 * else the Primary Entity of the object a binding drives. One output per case
 * (see portsFor), then `else`.
 */
export const switchNode = {
    kind: 'switch',
    label: 'Switch',
    icon: '⑂',
    color: '#9d174d',
    ports: {
        inputs: [ENTITY_INPUT],
        outputs: [CASE_OUTPUT(SWITCH_ELSE_PORT, 'Else')],
    },
    portsFor: data => ({
        inputs: [ENTITY_INPUT],
        outputs: [
            ...(data.cases ?? []).map(item => CASE_OUTPUT(switchCasePort(item.id), describeSwitchCase(item))),
            CASE_OUTPUT(SWITCH_ELSE_PORT, 'Else'),
        ],
    }),
    properties: {
        attribute: { type: 'string', label: 'Attribute' },
        cases: { type: 'list', label: 'Cases' },
    },
    defaults: { attribute: '', cases: [{ id: 'c1', operator: '==', value: 'on' }] },
}

/**
 * Trigger: runs its actions and condition branches when its source UI object
 * emits `event` (the events of the source's schema, schema.events), or when
 * the state of the entity wired to `source` changes — optionally `to` or
 * `from` a state, or of an `attribute` (a Home Assistant state trigger, for
 * example a GPIO input exposed by the openHASP integration).
 */
export const triggerNode = {
    kind: 'trigger',
    label: 'Trigger',
    icon: '⚡',
    color: '#d97706',
    ports: {
        inputs: [{ id: 'source', label: 'Object / entity', accepts: [HASS_PORT_KIND.OBJECT, HASS_PORT_KIND.ENTITY], max: 1 }],
        outputs: [{ id: 'run', label: 'Run', provides: HASS_PORT_KIND.RUN }],
    },
    properties: {
        event: { type: 'enum', label: 'Event', valuesFrom: 'sourceEvents' },
        to: { type: 'string', label: 'To state' },
        from: { type: 'string', label: 'From state' },
        attribute: { type: 'string', label: 'Attribute' },
    },
    defaults: { event: '' },
}

/**
 * Condition branch of a trigger. All branches of one trigger form one
 * `choose`: the first branch whose conditions all hold runs its actions.
 * `filter` is the btnmatrix button filter (`{ val: <index> }`). A condition
 * can compare an entity's state or attribute: the entity wired to `entity`,
 * or else the trigger source's Primary Entity.
 */
export const conditionNode = {
    kind: 'condition',
    label: 'Condition',
    icon: '?',
    color: '#a16207',
    ports: {
        inputs: [{ id: 'in', label: 'Trigger', accepts: [HASS_PORT_KIND.RUN], max: 1 }, ENTITY_INPUT],
        outputs: [{ id: 'then', label: 'Then', provides: HASS_PORT_KIND.BRANCH }],
    },
    properties: {
        conditions: { type: 'list', label: 'Conditions' },
        filter: { type: 'object', label: 'Button' },
    },
    defaults: { conditions: [], filter: null },
}

const ACTION_COLOR = '#0891b2'
const RUN_INPUT = Object.freeze({ id: 'run', label: 'Run', accepts: [HASS_PORT_KIND.RUN, HASS_PORT_KIND.BRANCH] })
const TARGET_OUTPUT = Object.freeze({ id: 'target', label: 'Target', provides: HASS_PORT_KIND.TARGET })

/**
 * Action nodes, one per AutomationAction type. A service call acts on the
 * entity nodes its `target` output is wired to; area/device/label targets
 * and dynamic targets stay in its data.
 */
export const HASS_ACTION_NODE_TYPES = {
    service: {
        kind: 'action', actionType: 'call_service', label: 'Action', icon: '▶', color: ACTION_COLOR,
        ports: { inputs: [RUN_INPUT], outputs: [TARGET_OUTPUT] },
    },
    delay: {
        kind: 'action', actionType: 'delay', label: 'Delay', icon: '⏱', color: '#475569',
        ports: { inputs: [RUN_INPUT], outputs: [] },
    },
}

/*
 * Action variants, following the task types of the action editor
 * (ActionEditor.vue TASK_TYPES). `taskType` in the preset tells the node
 * factory which action to build.
 */
const actionVariant = (id, nodeType, label, icon, taskType) =>
    ({ id: `action:${id}`, nodeType, label, icon, preset: taskType ? { taskType } : {} })

export const HASS_ACTION_VARIANTS = Object.freeze([
    actionVariant('toggle', 'service', 'Toggle', '⏻', 'toggle'),
    actionVariant('turn_on', 'service', 'Turn on', '●', 'turn_on'),
    actionVariant('turn_off', 'service', 'Turn off', '○', 'turn_off'),
    actionVariant('activate_scene', 'service', 'Activate scene', '✦', 'activate_scene'),
    actionVariant('tts', 'service', 'Text to speech', '🔊', 'media_player'),
    actionVariant('play_media', 'service', 'Media player', '♫', 'play_media'),
    actionVariant('delay', 'delay', 'Delay', '⏱', null),
    actionVariant('advanced', 'service', 'Action (Advanced)', '⌘', 'advanced'),
])
